@file:Suppress("NestedBlockDepth")

package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.ThreadLocalRef
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentitySet
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.jvm.JvmStatic

/**
 * 自动合批写入：把同一个执行批次内的多次响应式写入合并为一次订阅者通知。
 *
 * 嵌套语义：最外层入口 depth=1 → exit depth=0 才统一 flush。
 * 线程安全：所有状态托管在 ThreadLocal。
 */
object BatchScope {

    /** flush 迭代轮数上限。 */
    internal const val MAX_FLUSH_ROUNDS = 100

    private val depth: ThreadLocalRef<IntArray> = ThreadLocalRef { intArrayOf(0) }
    private val pending: ThreadLocalRef<MutableSet<Subscriber>> = ThreadLocalRef { newIdentitySet() }
    private val logger = UiLog("top.wcpe.allincore.ui.runtime.reactive.BatchScope")

    @JvmStatic fun inBatch(): Boolean = depth.get()[0] > 0

    @JvmStatic fun enter() {
        depth.get()[0]++
    }

    @JvmStatic
    fun exit() {
        val slot = depth.get()
        if (slot[0] <= 0) throw IllegalStateException("BatchScope.exit 被调用但当前不在批次中")
        slot[0]--
        if (slot[0] == 0) flush(slot)
    }

    @JvmStatic
    fun enqueue(subs: Collection<Subscriber>) {
        if (subs.isEmpty()) return
        pending.get().addAll(subs)
    }

    private fun flush(depthSlot: IntArray) {
        val p = pending.get()
        if (p.isEmpty()) return
        depthSlot[0]++
        var firstError: RuntimeException? = null
        try {
            var rounds = 0
            while (p.isNotEmpty()) {
                if (++rounds > MAX_FLUSH_ROUNDS) {
                    p.clear()
                    val loopErr = IllegalStateException(
                        "BatchScope flush 不收敛：超过 $MAX_FLUSH_ROUNDS 轮，疑似响应式循环",
                    )
                    if (firstError != null) loopErr.addSuppressed(firstError)
                    throw loopErr
                }
                val snapshot = ArrayList(p)
                p.clear()
                for (s in snapshot) {
                    try {
                        s.onChange()
                    } catch (ex: RuntimeException) {
                        logger.error("BatchScope flush 内订阅者抛错，已隔离", ex)
                        if (firstError == null) firstError = ex
                    }
                }
            }
        } finally {
            depthSlot[0]--
        }
        if (firstError != null) throw firstError
    }
}
